// Full details of a repair request — used in the foreman Review modal,
// the officer's "View" modal, and the Approvals page.
const safeJSON = (v) => { try { const a = JSON.parse(v || '[]'); return Array.isArray(a) ? a : []; } catch { return []; } };

const Row = ({ label, children }) => (
  <div className="flex text-sm py-1.5 border-b border-gray-100 last:border-0">
    <div className="w-44 shrink-0 font-medium text-gray-600">{label}</div>
    <div className="text-gray-800 capitalize-first">{children ?? '—'}</div>
  </div>
);

const fmtDT = (v) => v ? new Date(v).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

const ACTION_META = {
  approved:           { icon:'✅', cls:'text-green-700' },
  rejected:           { icon:'❌', cls:'text-red-600' },
  revision_requested: { icon:'✏️', cls:'text-blue-600' },
};

const INV_CLS = {
  pending_approval:'bg-yellow-100 text-yellow-800',
  pending_payment: 'bg-blue-100 text-blue-700',
  completed:       'bg-green-100 text-green-700',
  rejected:        'bg-red-100 text-red-700',
};

const Chip = ({ children }) => (
  <span className="inline-block px-2 py-0.5 mr-1 mb-1 rounded-full bg-primary-50 text-primary-700 text-xs">{children}</span>
);

export default function RepairDetailsCard({ repair, vehicle }) {
  if (!repair) return null;
  const repairItems  = safeJSON(repair.repair_items);
  const serviceItems = safeJSON(repair.service_items);
  const veh = vehicle || repair.vehicle;

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <Row label="Vehicle">{veh ? `${veh.reg_number} — ${veh.brand || ''} ${veh.model || ''}` : `#${repair.vehicle_id}`}</Row>
      {repair.driver_name && <Row label="Driver">{repair.driver_name}</Row>}
      <Row label="Title">{repair.title}</Row>
      <Row label="Repair Type"><span className="capitalize">{repair.repair_type}</span></Row>
      <Row label="Urgency"><span className="capitalize">{repair.urgency}</span></Row>
      <Row label="Status"><span className="capitalize">{(repair.status || '').replace('_',' ')}</span></Row>
      {repair.mileage_km && <Row label="Current Mileage">{repair.mileage_km} km</Row>}

      {repairItems.length > 0 && (
        <Row label="Repair Items"><div>{repairItems.map(i => <Chip key={i}>{i}</Chip>)}</div></Row>
      )}
      {serviceItems.length > 0 && (
        <Row label="Service Items"><div>{serviceItems.map(i => <Chip key={i}>{i}</Chip>)}</div></Row>
      )}
      {repair.repair_type === 'tyre' && repair.tyre_size && (
        <Row label="Tyre">{repair.tyre_size} × {repair.tyre_qty || 1}</Row>
      )}
      {repair.repair_type === 'battery' && repair.bat_size && (
        <Row label="Battery">{repair.bat_size} × {repair.bat_qty || 1}</Row>
      )}

      {repair.estimated_cost && <Row label="Estimated Cost">Rs. {Number(repair.estimated_cost).toLocaleString('en-LK', { minimumFractionDigits: 2 })}</Row>}
      {repair.actual_cost && <Row label="Actual Cost (paid)">Rs. {Number(repair.actual_cost).toLocaleString('en-LK', { minimumFractionDigits: 2 })}</Row>}
      <Row label="Requested By">{repair.requester?.name || repair.requested_by_name || '—'}</Row>
      <Row label="Submitted At">{fmtDT(repair.created_at)}</Row>
      <Row label="Last Updated">{fmtDT(repair.updated_at)}</Row>
      {repair.description && (
        <div className="pt-2 mt-1">
          <div className="text-sm font-medium text-gray-600 mb-1">Description</div>
          <div className="text-sm text-gray-700 whitespace-pre-wrap bg-white rounded border p-2">{repair.description}</div>
        </div>
      )}
      {repair.notes && (
        <div className="pt-2">
          <div className="text-sm font-medium text-gray-600 mb-1">Notes / Comments</div>
          <div className="text-sm text-gray-700 whitespace-pre-wrap bg-white rounded border p-2">{repair.notes}</div>
        </div>
      )}

      {Array.isArray(repair.approvals) && repair.approvals.length > 0 && (
        <div className="pt-3 mt-2 border-t">
          <div className="text-sm font-medium text-gray-600 mb-2">Approval History</div>
          <div className="space-y-2">
            {repair.approvals.map(a => (
              <div key={a.id} className="bg-white rounded border p-2 text-sm">
                <div className="flex justify-between flex-wrap gap-2">
                  <span className={ACTION_META[a.action]?.cls || ''}>
                    {ACTION_META[a.action]?.icon} <span className="capitalize font-medium">{a.action.replace('_',' ')}</span>
                    {' '}by {a.approver?.name || '—'}
                  </span>
                  <span className="text-xs text-gray-400">{fmtDT(a.action_date)}</span>
                </div>
                {a.comments && <p className="text-gray-600 text-xs mt-1 whitespace-pre-wrap">{a.comments}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(repair.invoices) && repair.invoices.length > 0 && (
        <div className="pt-3 mt-2 border-t">
          <div className="text-sm font-medium text-gray-600 mb-2">Invoices on this Job</div>
          <div className="space-y-1">
            {repair.invoices.map(i => (
              <div key={i.id} className="flex justify-between items-center bg-white rounded border p-2 text-sm flex-wrap gap-1">
                <span className="font-mono">{i.invoice_no}</span>
                <span>Rs. {Number(i.amount).toLocaleString('en-LK', { minimumFractionDigits: 2 })}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${INV_CLS[i.status] || ''}`}>{(i.status || '').replace('_',' ')}</span>
                {i.cheque_no && <span className="text-xs text-gray-400">Cheque {i.cheque_no}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
